import React, { useState, useRef, useEffect } from 'react';
import { Group, Rect, Text, Line } from 'react-konva';
import Konva from 'konva';
import { SequenceParticipant, ThemePalette, KonvaMouseEvent, KonvaInteractionEvent } from '../../types';
import { SEQUENCE } from '../../constants/sequence';
import { FONT_FAMILY } from '../../constants/typography';
import { blendColor } from '../../utils/color';
import { NodeIcon } from '../nodes/NodeIcon';

interface ParticipantComponentProps {
    participant: SequenceParticipant;
    lifelineBottom: number;
    topHeaderY?: number;
    bottomHeaderY?: number;
    stickyScale?: number;
    topPinned?: boolean;
    bottomPinned?: boolean;
    isSelected: boolean;
    onParticipantClick: (id: string, e: KonvaInteractionEvent) => void;
    onParticipantDoubleClick: (id: string) => void;
    onParticipantDragEnd: (id: string, newX: number) => void;
    onParticipantDragMove?: (id: string, newX: number) => void;
    onParticipantContextMenu?: (id: string, x: number, y: number) => void;
    palette: ThemePalette;
    onLifelineMouseDown: (id: string, y: number) => void;
    onLifelineMouseUp: (id: string, y: number) => void;
    layerOpacity?: number;
    hideLabel?: boolean;
    onParticipantHover?: (id: string | null) => void;
    isReadOnly?: boolean;
    canDrag?: boolean;
}

const HEADER_WIDTH = SEQUENCE.HEADER_WIDTH;
const HEADER_HEIGHT = SEQUENCE.HEADER_HEIGHT;
const HEADER_Y = SEQUENCE.HEADER_Y;

const ParticipantComponentInner: React.FC<ParticipantComponentProps> = ({
    participant,
    lifelineBottom,
    topHeaderY = HEADER_Y,
    bottomHeaderY,
    stickyScale = 1,
    topPinned = false,
    bottomPinned = false,
    isSelected,
    onParticipantClick,
    onParticipantDoubleClick,
    onParticipantDragEnd,
    onParticipantDragMove,
    onParticipantContextMenu,
    palette,
    onLifelineMouseDown,
    onLifelineMouseUp,
    layerOpacity = 1,
    hideLabel = false,
    onParticipantHover,
    isReadOnly = false,
    canDrag,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const canDragParticipant = canDrag ?? !isReadOnly;
    const groupRef = useRef<Konva.Group>(null);

    useEffect(() => {
        if (!groupRef.current) return;
        groupRef.current.opacity(0);
        groupRef.current.scale({ x: 0.85, y: 0.85 });
        const tw = new Konva.Tween({
            node: groupRef.current,
            duration: 0.15,
            opacity: layerOpacity,
            scaleX: 1,
            scaleY: 1,
            easing: Konva.Easings.EaseOut,
        });
        tw.play();
        return () => tw.destroy();
    }, [layerOpacity]);

    const topHeaderScale = topPinned ? stickyScale : 1;
    const bottomHeaderScale = bottomPinned ? stickyScale : 1;

    const topHeaderW = HEADER_WIDTH * topHeaderScale;
    const topHeaderH = HEADER_HEIGHT * topHeaderScale;
    const topHeaderX = (HEADER_WIDTH - topHeaderW) / 2;

    const targetBottomHeaderY = bottomHeaderY ?? lifelineBottom;
    const proposedBottomLocalY = targetBottomHeaderY - topHeaderY;
    const bottomHeaderLocalY = Math.max(topHeaderH + 120, proposedBottomLocalY);

    const bottomHeaderW = HEADER_WIDTH * bottomHeaderScale;
    const bottomHeaderH = HEADER_HEIGHT * bottomHeaderScale;
    const bottomHeaderX = (HEADER_WIDTH - bottomHeaderW) / 2;

    const lifelineStartY = topHeaderH;
    const lifelineEndY = bottomHeaderLocalY;
    const lifelineHitHeight = Math.max(24, lifelineEndY - lifelineStartY);

    const lifelineBaseStroke = blendColor(palette.edgeDefault, participant.color, 0.12, 0.78);
    const lifelineHoverStroke = blendColor(palette.edgeSelected, participant.color, 0.24, 0.95);

    const handleMouseEnter = () => { setIsHovered(true); onParticipantHover?.(participant.id); };
    const handleMouseLeave = () => { setIsHovered(false); onParticipantHover?.(null); };

    const handleContextMenu = (e: KonvaMouseEvent) => {
        e.evt.preventDefault();
        if (!onParticipantContextMenu) return;
        const stage = e.target.getStage();
        const pos = stage?.getPointerPosition();
        if (pos) onParticipantContextMenu(participant.id, pos.x, pos.y);
    };

    const topLabelPadding = Math.max(6, topHeaderScale * 8);
    const bottomLabelPadding = Math.max(6, bottomHeaderScale * 8);

    return (
        <Group
            ref={groupRef}
            x={participant.x}
            y={topHeaderY}
            opacity={layerOpacity}
            draggable={canDragParticipant}
            dragBoundFunc={(pos) => ({ x: pos.x, y: topHeaderY })}
            onDragMove={(e) => {
                e.target.y(topHeaderY);
                onParticipantDragMove?.(participant.id, e.target.x());
            }}
            onDragEnd={(e) => {
                const newX = e.target.x();
                e.target.y(topHeaderY);
                onParticipantDragEnd(participant.id, newX);
            }}
        >
            {isSelected && (
                <Rect
                    x={topHeaderX - 4}
                    y={-4}
                    width={topHeaderW + 8}
                    height={topHeaderH + 8}
                    cornerRadius={6}
                    stroke="#a855f7"
                    strokeWidth={2}
                    dash={[4, 4]}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                />
            )}

            <Rect
                x={topHeaderX + 1}
                y={2}
                width={topHeaderW}
                height={topHeaderH}
                fill={palette.nodeShadow}
                cornerRadius={6}
                listening={false}
                perfectDrawEnabled={false}
            />
            <Rect
                x={topHeaderX}
                width={topHeaderW}
                height={topHeaderH}
                fill={participant.color || palette.nodeBody}
                stroke={isSelected ? '#a855f7' : palette.nodeStroke}
                strokeWidth={isSelected ? 2 : 1}
                cornerRadius={6}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
            />

            <Rect
                x={topHeaderX}
                width={topHeaderW}
                height={topHeaderH}
                fill="transparent"
                perfectDrawEnabled={false}
                onClick={(e) => onParticipantClick(participant.id, e)}
                onTap={(e) => onParticipantClick(participant.id, e)}
                onDblClick={() => onParticipantDoubleClick(participant.id)}
                onDblTap={() => onParticipantDoubleClick(participant.id)}
                onContextMenu={handleContextMenu}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            />

            {participant.type && (
                <NodeIcon
                    type={participant.type}
                    accentColor={participant.color || '#6366f1'}
                    x={topHeaderX + topHeaderW}
                    y={0}
                    iconStyle="badge"
                />
            )}

            {!hideLabel && (
                <Text
                    text={participant.label}
                    x={topHeaderX + topLabelPadding}
                    y={0}
                    width={Math.max(40, topHeaderW - topLabelPadding * 2)}
                    height={topHeaderH}
                    align="center"
                    verticalAlign="middle"
                    fontFamily={FONT_FAMILY}
                    fontSize={Math.max(11, 13 * topHeaderScale)}
                    fill={palette.nodeText}
                    fontStyle="bold"
                    wrap="none"
                    ellipsis={true}
                    listening={false}
                    perfectDrawEnabled={false}
                />
            )}

            <Line
                points={[HEADER_WIDTH / 2, lifelineStartY, HEADER_WIDTH / 2, lifelineEndY]}
                stroke={isHovered ? lifelineHoverStroke : lifelineBaseStroke}
                strokeWidth={isHovered ? 3 : 2}
                dash={[8, 6]}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
            />

            {isHovered && (
                <Line
                    points={[HEADER_WIDTH / 2, lifelineStartY, HEADER_WIDTH / 2, lifelineEndY]}
                    stroke={lifelineHoverStroke}
                    strokeWidth={8}
                    opacity={0.14}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                />
            )}

            <Rect
                x={HEADER_WIDTH / 2 - 15}
                y={lifelineStartY}
                width={30}
                height={lifelineHitHeight}
                fill="transparent"
                perfectDrawEnabled={false}
                onMouseEnter={(e) => {
                    if (isReadOnly) return;
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'crosshair';
                }}
                onMouseLeave={(e) => {
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'default';
                }}
                onMouseDown={(e) => {
                    if (isReadOnly) return;
                    e.cancelBubble = true;
                    const pointer = e.target.getStage()?.getPointerPosition();
                    const viewportGroup = e.target.getParent()?.getParent();
                    const transform = viewportGroup?.getAbsoluteTransform().copy().invert();
                    let targetY = HEADER_HEIGHT + 100;
                    if (pointer && transform) {
                        targetY = transform.point(pointer).y;
                    }
                    onLifelineMouseDown(participant.id, targetY);
                }}
                onMouseUp={(e) => {
                    if (isReadOnly) return;
                    e.cancelBubble = true;
                    const pointer = e.target.getStage()?.getPointerPosition();
                    const viewportGroup = e.target.getParent()?.getParent();
                    const transform = viewportGroup?.getAbsoluteTransform().copy().invert();
                    let targetY = HEADER_HEIGHT + 100;
                    if (pointer && transform) {
                        targetY = transform.point(pointer).y;
                    }
                    onLifelineMouseUp(participant.id, targetY);
                }}
            />

            <Rect
                x={bottomHeaderX + 1}
                y={bottomHeaderLocalY + 2}
                width={bottomHeaderW}
                height={bottomHeaderH}
                fill={palette.nodeShadow}
                cornerRadius={6}
                listening={false}
                perfectDrawEnabled={false}
            />
            <Rect
                x={bottomHeaderX}
                y={bottomHeaderLocalY}
                width={bottomHeaderW}
                height={bottomHeaderH}
                fill={participant.color || palette.nodeBody}
                stroke={isSelected ? '#a855f7' : palette.nodeStroke}
                strokeWidth={isSelected ? 2 : 1}
                cornerRadius={6}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
            />

            <Rect
                x={bottomHeaderX}
                y={bottomHeaderLocalY}
                width={bottomHeaderW}
                height={bottomHeaderH}
                fill="transparent"
                perfectDrawEnabled={false}
                onClick={(e) => onParticipantClick(participant.id, e)}
                onTap={(e) => onParticipantClick(participant.id, e)}
                onDblClick={() => onParticipantDoubleClick(participant.id)}
                onDblTap={() => onParticipantDoubleClick(participant.id)}
                onContextMenu={handleContextMenu}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            />

            {participant.type && (
                <NodeIcon
                    type={participant.type}
                    accentColor={participant.color || '#6366f1'}
                    x={bottomHeaderX + bottomHeaderW}
                    y={bottomHeaderLocalY}
                    iconStyle="badge"
                />
            )}

            {!hideLabel && (
                <Text
                    text={participant.label}
                    x={bottomHeaderX + bottomLabelPadding}
                    y={bottomHeaderLocalY}
                    width={Math.max(40, bottomHeaderW - bottomLabelPadding * 2)}
                    height={bottomHeaderH}
                    align="center"
                    verticalAlign="middle"
                    fontFamily={FONT_FAMILY}
                    fontSize={Math.max(11, 13 * bottomHeaderScale)}
                    fill={palette.nodeText}
                    fontStyle="bold"
                    wrap="none"
                    ellipsis={true}
                    listening={false}
                    perfectDrawEnabled={false}
                />
            )}

            {isSelected && (
                <Rect
                    x={bottomHeaderX - 4}
                    y={bottomHeaderLocalY - 4}
                    width={bottomHeaderW + 8}
                    height={bottomHeaderH + 8}
                    cornerRadius={6}
                    stroke="#a855f7"
                    strokeWidth={2}
                    dash={[4, 4]}
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowForStrokeEnabled={false}
                />
            )}
        </Group>
    );
};

export const ParticipantComponent = React.memo(ParticipantComponentInner);

export { HEADER_WIDTH, HEADER_HEIGHT, HEADER_Y };
